import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Alert,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

import { RootState } from "../../store/Store";
import {
  fetchData,
  putData,
  FetchDataSuccessAction,
  FetchDataFailureAction,
} from "../../store/actions";
import {
  ToastVariant,
  useCustomToast,
} from "../../components/Methods/SnackBar";
import Navbar from "../../components/Page Component/Navbar";
import Footer from "../../components/Page Component/Footer";
import { setIsLoading } from "../../store/LoadingSlice";
import { processComplianceAssignmentsUrl } from "../../Service/Apis";
import ConfirmDialog from "../../components/Dialogs/ConfirmDialog";
import AlertDialog from "../../components/Dialogs/AlertDialog";
import { titleCase, formatDateToDDMMYYYY } from "./utils";
import ComplianceFormSkeleton from "../../components/Skeleton/ComplianceFormSkeleton";
import PageTitle from "../../components/Page Component/PageTitle";
import {
  canEditAssignment,
  isComplianceAdmin,
  isLinkedTeamViewer,
} from "../../utils/compliancePermissions";

// Updated interfaces for the new structure
interface Question {
  id: string;
  question: string;
  department?: string;
}

interface SprintName {
  id: string;
  name: string;
}

interface SprintResponse {
  value: "Yes" | "No" | "N/A" | "";
  comments: string;
}

interface Department {
  id: string;
  name: string;
  percentage: number;
}

interface ComplianceAssignment {
  assignment_id: string;
  project: string;
  project_name: string;
  template: string;
  template_name: string;
  assigned_to: string;
  assigned_to_name: string;
  assigned_by: string;
  assigned_by_name: string;
  due_date: string;
  due_date_str: string;
  completion_date: string | null;
  status: "pending" | "in_progress" | "completed" | "overdue";
  assignment_type: "direct" | "project_based";
  month_year: string;
  month_year_str: string;
  data: Record<string, Record<string, SprintResponse>>;
  template_data: {
    questions: Question[];
    sprintCount: number;
    sprintNames: SprintName[];
    departments?: Department[];
    categories?: Department[];
  };
  compliance_score?: {
    overall_score: number;
    category_scores: Record<string, {
      name: string;
      percentage_weight: number;
      yes_count: number;
      no_count: number;
      total_answered: number;
      score: number;
    }>;
    total_yes: number;
    total_no: number;
    total_answered: number;
  };
}

const ProcessComplianceForm: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const [isProfileLoaded, setIsProfileLoaded] = useState(false);
  const personId = useSelector((state: RootState) => state.person.perstate.id);
  const [assignment, setAssignment] = useState<ComplianceAssignment | null>(
    null
  );
  const [formData, setFormData] = useState<
    Record<string, Record<string, SprintResponse>>
  >({});
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [tempFormData, setTempFormData] = useState<
    Record<string, Record<string, SprintResponse>>
  >({});
  const [permissionAlertOpen, setPermissionAlertOpen] = useState(false);

  const showToast = useCustomToast();
  const dispatch = useDispatch();
  const dispatch2: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const selectedRole = useSelector(
    (state: RootState) => state.auth.selectedRole
  );

  useEffect(() => {
  if (personId) {
    setIsProfileLoaded(true);
  }
}, [personId]);


  useEffect(() => {
    setIsLoading(true);
    if (assignmentId && isProfileLoaded) {
      fetchAssignment();
    }
  }, [assignmentId, isProfileLoaded]);

  // Re-evaluate edit rights when role or profile changes after the assignment loaded
  useEffect(() => {
    if (!assignment) return;
    setIsReadOnly(
      !canEditAssignment({
        selectedRole,
        personId,
        assignedTo: assignment.assigned_to,
        status: assignment.status,
        assignmentType: assignment.assignment_type,
      })
    );
  }, [selectedRole, personId, assignment]);

  useEffect(() => {
    // Handle legacy templates that have neither departments nor categories
    if (
      assignment &&
      assignment.template_data &&
      !assignment.template_data.departments &&
      !assignment.template_data.categories
    ) {
      // Create a default department for all questions
      const updatedAssignment = {
        ...assignment,
        template_data: {
          ...assignment.template_data,
          departments: [
            { id: "default", name: "General Compliance", percentage: 100 },
          ],
        },
      };

      // Assign all questions to default department
      updatedAssignment.template_data.questions =
        updatedAssignment.template_data.questions.map((q) => ({
          ...q,
          department: "default",
        }));

      setAssignment(updatedAssignment);
    }
  }, [assignment]);

  const getQuestionsGroupedByDepartment = (): {
    departments: Department[];
    groupedQuestions: Record<string, Question[]>;
  } => {
    if (!assignment || !assignment.template_data)
      return { departments: [], groupedQuestions: {} };

    // Prefer new categories system; fall back to legacy departments
    const useCategories = !!(assignment.template_data.categories?.length);
    const departments: Department[] = useCategories
      ? (assignment.template_data.categories as Department[])
      : (assignment.template_data.departments || []);

    const groupedQuestions: Record<string, Question[]> = {};

    departments.forEach((dept) => {
      groupedQuestions[dept.id] = [];
    });

    groupedQuestions["unassigned"] = [];

    assignment.template_data.questions.forEach((question) => {
      const key = useCategories
        ? (question as any).category
        : question.department;
      if (key && groupedQuestions[key]) {
        groupedQuestions[key].push(question);
      } else {
        groupedQuestions["unassigned"].push(question);
      }
    });

    return { departments, groupedQuestions };
  };

  const fetchAssignment = async () => {
    if (!isProfileLoaded) {
    return; // Don't proceed if profile isn't loaded
  }
    try {
      dispatch(setIsLoading(true));
      const response = await dispatch2(
        fetchData(
          `${processComplianceAssignmentsUrl}${assignmentId}/?role=${selectedRole}`
        )
      );

      // Normalise response — the list endpoint returns an array; resolve to one item.
      let assignmentData: any = null;

      if (Array.isArray(response)) {
        assignmentData = response.find(
          (item: any) => item.assignment_id === assignmentId
        ) as ComplianceAssignment;

        if (!assignmentData) {
          console.error("Assignment not found in response array:", response);
          showToast("Error: Assignment not found", "error", true);
          dispatch(setIsLoading(false));
          navigate("/process-compliance-assignments");
          return;
        }
      } else {
        assignmentData = response as ComplianceAssignment;
      }

      // Check if the assignment was found
      if (!assignmentData || !assignmentData.assignment_id) {
        console.error("No assignment found with this ID", assignmentId);
        showToast("Error: Assignment not found", "error", true);
        dispatch(setIsLoading(false));
        navigate("/process-compliance-assignments");
        return;
      }

      // Check if template_data exists, if not, handle gracefully
      if (!assignmentData.template_data) {
        console.error(
          "Missing template_data in assignment response:",
          assignmentData
        );
        showToast("Error: Assignment has missing template data", "error", true);
        dispatch(setIsLoading(false));
        navigate("/process-compliance-assignments");
        return;
      }

      setAssignment(assignmentData);

      // Load existing data or initialize empty data structure
      if (assignmentData.data && Object.keys(assignmentData.data).length > 0) {
        setFormData(assignmentData.data);
      } else {
        const initialData: Record<string, Record<string, SprintResponse>> = {};

        // Check if template data has questions and sprintCount
        const questions = assignmentData.template_data?.questions || [];
        const sprintCount = assignmentData.template_data?.sprintCount || 0;

        // Initialize data structure based on template
        questions.forEach((question: any) => {
          initialData[question.id] = {};

          // Create entries for each sprint
          for (let i = 1; i <= sprintCount; i++) {
            initialData[question.id][`sprint${i}`] = {
              value: "",
              comments: "",
            };
          }
        });

        setFormData(initialData);
      }

      // Determine edit rights using the shared permission helper.
      // personId is guaranteed to be loaded here because fetchAssignment only runs
      // after isProfileLoaded becomes true (guarded by the useEffect dependency).
      setIsReadOnly(
        !canEditAssignment({
          selectedRole,
          personId,
          assignedTo: assignmentData.assigned_to,
          status: assignmentData.status,
          assignmentType: assignmentData.assignment_type,
        })
      );
    } catch (error) {
      console.error("Error fetching assignment:", error);
      showToast("Error fetching assignment details", "error", true);
      navigate("/process-compliance-assignments");
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const handleValueChange = (
    questionId: string,
    sprintKey: string,
    value: "Yes" | "No" | "N/A" | ""
  ) => {
    setFormData((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [sprintKey]: {
          ...(prev[questionId]?.[sprintKey] || {}),
          value,
        },
      },
    }));
  };

  const handleCommentChange = (
    questionId: string,
    sprintKey: string,
    comments: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [sprintKey]: {
          ...(prev[questionId]?.[sprintKey] || {}),
          comments,
        },
      },
    }));
  };

  const handleSubmit = async () => {
    if (!assignment) return;

    if (
      !canEditAssignment({
        selectedRole,
        personId,
        assignedTo: assignment.assigned_to,
        status: assignment.status,
        assignmentType: assignment.assignment_type,
      })
    ) {
      setPermissionAlertOpen(true);
      return;
    }

    // Validate that No/N/A responses have a comment
    const missingRequiredComment = Object.entries(formData).some(
      ([, sprints]) =>
        Object.values(sprints).some(
          (r) =>
            (r.value === "No" || r.value === "N/A") && !r.comments?.trim()
        )
    );

    if (missingRequiredComment) {
      showToast(
        "Comment is required for all No and N/A responses",
        "error",
        true
      );
      return;
    }

    // Validate form data
    const invalidEntries = Object.entries(formData).some(
      ([questionId, sprints]) =>
        Object.entries(sprints).some(([sprintKey, response]) => !response.value)
    );

    if (invalidEntries) {
      // Store form data temporarily and show confirmation dialog
      setTempFormData(formData);
      setConfirmDialogOpen(true);
    } else {
      // If no invalid entries, proceed with submission
      await submitFormData(formData);
    }
  };

  const submitFormData = async (
    dataToSubmit: Record<string, Record<string, SprintResponse>>
  ) => {
    try {
      dispatch(setIsLoading(true));

      // Determine status based on completion
      const newStatus = Object.values(dataToSubmit).every((sprints) =>
        Object.values(sprints).every((response) => response.value)
      )
        ? "completed"
        : "in_progress";

      const updateData = {
        status: newStatus,
        data: dataToSubmit,
        completion_date:
          newStatus === "completed"
            ? new Date().toISOString().split("T")[0]
            : null,
      };

      // putData now returns the server response body which contains the
      // freshly-computed compliance_score — use it directly so the score
      // section updates in the same render cycle without a second GET.
      const updatedAssignment = await dispatch2(
        putData(
          `${processComplianceAssignmentsUrl}${assignmentId}/?role=${selectedRole}`,
          updateData
        )
      );

      showToast("Compliance form saved successfully", "success", true);

      if (updatedAssignment && updatedAssignment.assignment_id) {
        // Update local state from PUT response — no extra GET needed
        setAssignment(updatedAssignment as ComplianceAssignment);
        setFormData(updatedAssignment.data || dataToSubmit);
      } else {
        // Fallback: re-fetch if PUT response didn't contain the full object
        await fetchAssignment();
      }

      if (newStatus === "completed") {
        setIsReadOnly(true);
      }
    } catch (error) {
      console.error("Error saving form:", error);
      showToast("Error saving compliance form", "error", true);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "#2e7d32";
      case "in_progress":
        return "#1565c0";
      case "pending":
        return "#e65100";
      case "overdue":
        return "#c62828";
      default:
        return "#546e7a";
    }
  };

  const renderConfirmDialog = () => (
    <ConfirmDialog
      open={confirmDialogOpen}
      title="Incomplete Form"
      message="Some fields are empty. Do you want to save anyway? The form will be marked as 'In Progress'."
      severity="warning"
      confirmButtonText="Save Anyway"
      cancelButtonText="Cancel"
      onConfirm={() => {
        setConfirmDialogOpen(false);
        submitFormData(tempFormData);
      }}
      onCancel={() => {
        setConfirmDialogOpen(false);
      }}
    />
  );

  const renderPermissionAlert = () => (
    <AlertDialog
      open={permissionAlertOpen}
      title="Permission Denied"
      message="Only the assigned user, Process Compliance Officer, or an administrator can submit this form."
      severity="error"
      onClose={() => {
        setPermissionAlertOpen(false);
      }}
    />
  );

  if (!assignment) {
    return (
      <>
        <Container maxWidth="lg" sx={{ mt: 12, mb: 10 }}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <ComplianceFormSkeleton />
          </Paper>
        </Container>
      </>
    );
  }

  // Get sprint names or generate default ones
  const getSprintNames = () => {
    if (
      assignment.template_data.sprintNames &&
      assignment.template_data.sprintNames.length > 0
    ) {
      return assignment.template_data.sprintNames;
    }

    // Generate default sprint names if not available
    return Array.from(
      { length: assignment.template_data.sprintCount },
      (_, i) => ({ id: `sprint${i + 1}`, name: `Sprint ${i + 1}` })
    );
  };

  const sprintNames = getSprintNames();

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 12, mb: 8 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <PageTitle>Process Compliance Form</PageTitle>
            <Chip
              label={
                // titleCase(assignment.status.charAt(0).toUpperCase()) +
                titleCase(assignment.status)
              }
              size="medium"
              sx={{
                backgroundColor: getStatusColor(assignment.status),
                color: "white",
              }}
            />
          </Box>

          {isReadOnly && assignment.status === "completed" && (
            <Alert severity="success" sx={{ mb: 3 }}>
              This compliance form has been submitted. You have view-only access.
            </Alert>
          )}

          {isReadOnly && assignment.status !== "completed" &&
            isLinkedTeamViewer({ selectedRole, personId, assignedTo: assignment.assigned_to }) && (
            <Alert severity="info" sx={{ mb: 3 }}>
              You are viewing this compliance submission as a linked team member. Only the assigned person can edit it.
            </Alert>
          )}

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Project
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignment.assignment_type === 'direct' ? '—' : assignment.project_name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Template
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignment.template_name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Period
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignment.month_year_str}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Assigned To
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignment.assigned_to_name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Due Date
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignment.due_date ? formatDateToDDMMYYYY(new Date(assignment.due_date)) : "—"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Completion Date
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {assignment.completion_date
                  ? formatDateToDDMMYYYY(new Date(assignment.completion_date))
                  : "Not completed"}
              </Typography>
            </Grid>
          </Grid>

          {/* Department Summary Cards */}
          <Box sx={{ mb: 3, mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
              Compliance Categories and Weights
            </Typography>
            <Grid container spacing={2}>
              {assignment.template_data.departments?.map((dept) => (
                <Grid item xs={12} sm={6} md={3} key={dept.id}>
                  <Box
                    sx={{
                      p: 1.5,
                      border: "1px solid #c8e6c9",
                      borderRadius: 1,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body1" fontWeight="medium">
                        {dept.name}
                      </Typography>
                      <Chip
                        label={`${dept.percentage}%`}
                        size="small"
                        sx={{
                          fontWeight: "bold",
                          backgroundColor: "#007300",
                          color: "#fff",
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mt: 1 }}
                    >
                      {
                        assignment.template_data.questions.filter(
                          (q) => q.department === dept.id
                        ).length
                      }{" "}
                      questions
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Compliance Scores Section */}
          {assignment.compliance_score && assignment.compliance_score.total_answered > 0 && (
            <Box sx={{ mb: 3, mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                Compliance Scores
              </Typography>
              
              {/* Category Scores */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {Object.entries(assignment.compliance_score.category_scores).map(([catId, catScore]) => (
                  <Grid item xs={12} sm={6} md={3} key={catId}>
                    <Box sx={{ p: 2, border: '1px solid #c8e6c9', borderRadius: 1, bgcolor: '#fafafa' }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {catScore.name}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 0.5 }}>
                        {catScore.yes_count} / {catScore.total_answered}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {catScore.score.toFixed(1)}% (count of (Yes) / count of (Yes+No))
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              
              {/* Overall Score */}
              <Box sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #c8e6c9' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Overall Compliance Score
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {assignment.compliance_score.total_yes} / {assignment.compliance_score.total_answered}&nbsp;
                  ({assignment.compliance_score.overall_score.toFixed(1)}%)
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Yes responses out of total Yes + No answered (N/A excluded from score)
                </Typography>
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Form Table with fixed left column and horizontal scrolling */}
          <Box sx={{ position: "relative", overflowX: "auto" }}>
            <Table sx={{ tableLayout: "fixed", minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#e8f5e9" }}>
                  {/* Fixed Guidelines Column */}
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      width: "240px",
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#e8f5e9",
                      zIndex: 2,
                      borderRight: "1px solid #c8e6c9",
                      boxShadow: "2px 0 4px -2px rgba(0,0,0,0.08)",
                    }}
                  >
                    Guidelines
                  </TableCell>

                  {/* Create header cells for each sprint - 2 columns per sprint (value + comment) */}
                  {sprintNames.map((sprint, idx) => (
                    <React.Fragment key={`header-${sprint.id}`}>
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: "bold",
                          width: "120px",
                        }}
                      >
                        {sprint.name}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: "bold",
                          width: "200px",
                          borderRight:
                            idx < sprintNames.length - 1
                              ? "1px solid #c8e6c9"
                              : "none",
                        }}
                      >
                        Comments
                      </TableCell>
                    </React.Fragment>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const { departments, groupedQuestions } =
                    getQuestionsGroupedByDepartment();

                  return departments
                    .concat({
                      id: "unassigned",
                      name: "No Category",
                      percentage: 0,
                    })
                    .map((department: any) => {
                      const questionsInDept = groupedQuestions[department.id];

                      // Skip departments with no questions
                      if (!questionsInDept || questionsInDept.length === 0)
                        return null;

                      return (
                        <React.Fragment key={`dept-${department.id}`}>
                          {/* Department Header Row */}
                          <TableRow>
                            <TableCell
                              colSpan={
                                1 + assignment.template_data.sprintCount * 2
                              }
                              sx={{
                                backgroundColor: "#e8f5e9",
                                color: "#1b5e20",
                                fontWeight: "bold",
                                position: "sticky",
                                left: 0,
                                zIndex: 0,
                                borderLeft: "4px solid #007300",
                              }}
                            >
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Typography
                                  variant="subtitle1"
                                  fontWeight="bold"
                                >
                                  {department.name}
                                </Typography>
                                {department.id !== "unassigned" && (
                                  <Chip
                                    label={`${department.percentage}%`}
                                    size="small"
                                    sx={{
                                      backgroundColor: "white",
                                      fontWeight: "bold",
                                    }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* Questions for this department */}
                          {questionsInDept.map((question: any, qIdx: any) => (
                            <TableRow
                              key={question.id}
                              sx={{
                                "&:nth-of-type(odd)": {
                                  backgroundColor: "#fafafa",
                                },
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                              }}
                            >
                              {/* Fixed Guidelines Cell */}
                              <TableCell
                                component="th"
                                scope="row"
                                sx={{
                                  verticalAlign: "top",
                                  borderRight: "1px solid #c8e6c9",
                                  padding: 2,
                                  position: "sticky",
                                  left: 0,
                                  backgroundColor:
                                    qIdx % 2 === 0 ? "#fafafa" : "#ffffff",
                                  zIndex: 1,
                                  boxShadow: "2px 0 4px -2px rgba(0,0,0,0.08)",
                                }}
                              >
                                {question.question}
                              </TableCell>

                              {/* Create cells for each sprint (value + comment) */}
                              {Array.from(
                                {
                                  length: assignment.template_data.sprintCount,
                                },
                                (_, i) => {
                                  const sprintKey = `sprint${i + 1}`;
                                  const sprintName =
                                    sprintNames[i]?.name || `Sprint ${i + 1}`;

                                  return (
                                    <React.Fragment
                                      key={`${question.id}-${sprintKey}`}
                                    >
                                      <TableCell
                                        align="center"
                                        sx={{
                                          verticalAlign: "top",
                                          padding: 1,
                                        }}
                                      >
                                        <FormControl
                                          size="small"
                                          fullWidth
                                          disabled={isReadOnly}
                                          sx={{ minWidth: 110, zIndex: 0 }}
                                        >
                                          <InputLabel
                                            id={`label-${question.id}-${sprintKey}`}
                                          >
                                            Response
                                          </InputLabel>
                                          <Select
                                            labelId={`label-${question.id}-${sprintKey}`}
                                            value={
                                              formData[question.id]?.[sprintKey]
                                                ?.value || ""
                                            }
                                            onChange={(e) =>
                                              handleValueChange(
                                                question.id,
                                                sprintKey,
                                                e.target.value as
                                                  | "Yes"
                                                  | "No"
                                                  | "N/A"
                                                  | ""
                                              )
                                            }
                                            label="Response"
                                          >
                                            <MenuItem value="">
                                              <em>Select...</em>
                                            </MenuItem>
                                            <MenuItem value="Yes">Yes</MenuItem>
                                            <MenuItem value="No">No</MenuItem>
                                            <MenuItem value="N/A">N/A</MenuItem>
                                          </Select>
                                        </FormControl>
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          verticalAlign: "top",
                                          padding: 1,
                                          borderRight:
                                            i <
                                            assignment.template_data
                                              .sprintCount -
                                              1
                                              ? "1px solid #c8e6c9"
                                              : "none",
                                        }}
                                      >
                                        {(() => {
                                          const resp = formData[question.id]?.[sprintKey];
                                          const needsComment = resp?.value === "No" || resp?.value === "N/A";
                                          const hasComment = !!resp?.comments?.trim();
                                          return (
                                            <TextField
                                              size="small"
                                              variant="outlined"
                                              fullWidth
                                              multiline
                                              rows={2}
                                              placeholder={needsComment ? "Comment required *" : "Comments"}
                                              value={resp?.comments || ""}
                                              onChange={(e) =>
                                                handleCommentChange(
                                                  question.id,
                                                  sprintKey,
                                                  e.target.value
                                                )
                                              }
                                              disabled={isReadOnly}
                                              error={!isReadOnly && needsComment && !hasComment}
                                              helperText={!isReadOnly && needsComment && !hasComment ? "Required for No and N/A" : undefined}
                                              InputProps={{
                                                sx: {
                                                  backgroundColor: isReadOnly
                                                    ? "#f5f5f5"
                                                    : "white",
                                                },
                                              }}
                                            />
                                          );
                                        })()}
                                      </TableCell>
                                    </React.Fragment>
                                  );
                                }
                              )}
                            </TableRow>
                          ))}
                        </React.Fragment>
                      );
                    });
                })()}
              </TableBody>
            </Table>
          </Box>

          {!isReadOnly && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSubmit}>
                Save
              </Button>
            </Box>
          )}

          {isReadOnly && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button variant="outlined" onClick={() => navigate(-1)}>
                Back
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
      {renderConfirmDialog()}
      {renderPermissionAlert()}
    </>
  );
};

export default ProcessComplianceForm;
